import { Box, Button, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import CrimeList from "../../components/CrimeList";
import CrimeCaughtPopup from "../../components/CrimePopup/CrimeCaughtPopup";
import CrimeFailedPopup from "../../components/CrimePopup/CrimeFailedPopup";
import CrimeSuccessPopup from "../../components/CrimePopup/CrimeSuccessPopup";
import HelperBotWorkingPopup from "../../components/HelperBotPopup/HelperBotWorkingPopup";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import clockIcon from "../../assets/imgs/crime/clockwise.png";

import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import useCrimeSSE from "./hooks/useCrimeSSE";

import { RankXP } from "../../constants/rank";
import {
  finishCrime,
  getJailedUntilAction,
  getNextCrimeTimeAction,
  getUserHelperBotInfo,
  makeCrimeAction,
} from "../../reducers/game.slice";
import { getMyProfile } from "../../reducers/profile.slice";
import { convertDateTimeSimple } from "../../utils/utils";

import useStyles from "./index.styles";

const Crime = () => {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const { account } = useWallet();
  const currentTime = useCurrentTime();

  const { myProfile } = useAppSelector((state) => state.profile);
  const { nextCrimeTime, jailedUntil, userCrimeBotInfo } = useAppSelector(
    (state) => state.game
  );
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { updateBalance } = useGameBankBalance(account, signMsg, signature);

  const [openFailedModal, setOpenFailedModal] = useState(false);
  const [openCaughtModal, setOpenCaughtModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openBotWorkingPopup, setOpenBotWorkingPopup] = useState(false);

  const [successCashAmount, setSuccessCashAmount] = useState(0);
  const [successXpAmount, setSuccessXpAmount] = useState(0);
  const [successXpPercent, setSuccessXpPercent] = useState(0);

  const renderStatus = () => {
    if (currentTime < jailedUntil) {
      return "You are in jail now!";
    }

    if (currentTime > nextCrimeTime) {
      return "Ready to Crime! 🎯";
    }

    const timeRemaining = convertDateTimeSimple(nextCrimeTime - currentTime);
    return `Next crime in: ${timeRemaining}`;
  };

  const handleAttempt = (crimeType: number) => {
    dispatch(makeCrimeAction({ account, crimeType }));
  };

  // Memoize the callback functions for useCrimeSSE
  const onSuccess = useCallback(
    (cashAmount: number, xpAmount: number) => {
      setOpenSuccessModal(true);
      setSuccessCashAmount(cashAmount);
      setSuccessXpAmount(xpAmount);

      updateBalance(account, signMsg, signature);
      dispatch(getNextCrimeTimeAction(account));
      dispatch(finishCrime());

      if (myProfile.rankNumber && myProfile.rankNumber < RankXP.length) {
        const xpPercent =
          (xpAmount /
            (RankXP[myProfile.rankNumber] - RankXP[myProfile.rankNumber - 1])) *
          100;

        setSuccessXpPercent(xpPercent);
      }
      myProfile.name && dispatch(getMyProfile(myProfile.name));
    },
    [
      account,
      dispatch,
      myProfile.name,
      myProfile.rankNumber,
      signMsg,
      signature,
      updateBalance,
    ]
  );

  const onCaught = useCallback(() => {
    setOpenCaughtModal(true);
    dispatch(getNextCrimeTimeAction(account));
    dispatch(finishCrime());
  }, [account, dispatch]);

  const onFailed = useCallback(() => {
    setOpenFailedModal(true);
    dispatch(getNextCrimeTimeAction(account));
    dispatch(finishCrime());
  }, [account, dispatch]);

  const onBotWorking = useCallback(() => {
    // setOpenBotWorkingPopup(true);
    dispatch(getNextCrimeTimeAction(account));
    dispatch(finishCrime());
  }, [account, dispatch]);

  useCrimeSSE(account, onSuccess, onCaught, onFailed, onBotWorking);

  useEffect(() => {
    dispatch(getNextCrimeTimeAction(account));
    dispatch(getJailedUntilAction(account));
    dispatch(getUserHelperBotInfo(account));
  }, [account, dispatch]);

  useEffect(() => {
    if (userCrimeBotInfo.isRunning) {
      // setOpenBotWorkingPopup(true);
    }
  }, [userCrimeBotInfo]);

  return (
    <Box className={classes.body}>
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Typography
            fontFamily={"Philosopher"}
            color={"white"}
            textAlign={"center"}
            fontSize={36}
            fontWeight={700}
          >
            Make a crime
          </Typography>

          <Button
            startIcon={<Box component="img" src={clockIcon} />}
            variant="contained"
            color="primary"
            size="large"
            className={classes.headerButton}
          >
            {renderStatus()}
          </Button>
        </Box>

        <CrimeList
          handleAttempt={handleAttempt}
          disabled={currentTime < nextCrimeTime || currentTime < jailedUntil}
        />
      </Box>

      <CrimeFailedPopup
        openModal={openFailedModal}
        setOpenModal={setOpenFailedModal}
      />

      <CrimeCaughtPopup
        openModal={openCaughtModal}
        setOpenModal={setOpenCaughtModal}
      />

      <CrimeSuccessPopup
        xpAmount={successXpAmount}
        xpPercent={successXpPercent}
        cashAmount={successCashAmount}
        openModal={openSuccessModal}
        setOpenModal={setOpenSuccessModal}
      />

      <HelperBotWorkingPopup
        openModal={openBotWorkingPopup}
        setOpenModal={setOpenBotWorkingPopup}
      />
    </Box>
  );
};

export default Crime;
